/**
 * CO2 Dashboard — customer-facing carbon-footprint analytics page,
 * modeled after the Cocoon Carbon dashboard the user shared.
 *
 * KPIs: Distance, Weight, # Shipments, CO₂ Emissions, # Trees, CO₂ Offset
 * Charts:
 *   • Movement count by mode (donut)
 *   • Emissions breakdown (donut)
 *   • Last 12 months CO₂ by week (stacked bar)
 *   • Top trade lanes (table)
 *
 * Also lets the user trigger an emailed report for any period.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import {
  Leaf, Truck, Plane, Ship, Train, Package, TreeDeciduous,
  MapPin, Send, Download, RefreshCw, Loader2, ChevronDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/Navbar";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_COLORS = {
  sea:   "#2563eb",
  air:   "#dc2626",
  road:  "#ea580c",
  rail:  "#0ea5e9",
  barge: "#7c3aed",
};
const MODE_ICONS = { sea: Ship, air: Plane, road: Truck, rail: Train, barge: Ship };

function Kpi({ icon: Icon, label, value, accent = "indigo", subtitle }) {
  return (
    <div className="bg-white rounded-xl p-4 border shadow-sm flex items-start gap-4" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className={`w-12 h-12 rounded-lg bg-${accent}-100 flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-6 h-6 text-${accent}-600`} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-900 truncate">{value}</p>
        {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      </div>
    </div>
  );
}

export default function CO2DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("ytd");
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [sending, setSending] = useState(false);
  // 2026-05-16: staff can filter by customer
  const [customerFilter, setCustomerFilter] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const token = localStorage.getItem("token");

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/customer-portal/co2-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          period,
          company_filter: branch || undefined,
          customer: customerFilter || undefined,
        },
      });
      setData(r.data);
      // Populate customer dropdown from top_customers on first staff load
      if (r.data?.is_staff_view && !customerFilter && r.data.top_customers?.length) {
        setCustomerOptions(r.data.top_customers.map(c => c.name));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  }, [token, period, branch, customerFilter]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/customer-portal/my-companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBranches(r.data?.companies || []);
      } catch { setBranches([]); }
    })();
  }, [token]);

  const sendReport = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/customer-portal/co2-report/send`,
        { period, company_filter: branch || undefined },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Report sent to ${r.data.recipient} ✓`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send report");
    } finally { setSending(false); }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center items-center min-h-[40vh] pt-20">
          <Loader2 className="w-10 h-10 animate-spin text-green-600" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="p-6 pt-20 text-slate-500">No data.</div>
      </div>
    );
  }

  const k = data.kpis;
  const byMode = Object.entries(data.by_mode_kg || {}).map(([mode, kg]) => ({
    name: mode, value: Math.round(kg),
  }));
  const movementsByMode = Object.entries(data.movements_by_mode || {}).map(([mode, count]) => ({
    name: mode, value: count,
  }));

  const periodLabel = {
    ytd: "Year-To-Date", "12m": "Last 12 Months", "month": "This Month",
  }[period] || period;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="space-y-6 p-4 md:p-6 pt-20" data-testid="co2-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80">
              {data.is_staff_view ? "Staff · Sustainability" : "Sustainability"}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mt-1">
              {data.is_staff_view
                ? (customerFilter ? `CO₂ Dashboard — ${customerFilter}` : "Company-wide CO₂ Dashboard")
                : "Your CO₂ Dashboard"}
            </h1>
            <p className="text-sm opacity-90 mt-1">
              {periodLabel} · {k.shipments} shipments · methodology: GLEC v3.0
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-white/15 border border-white/30 text-white px-3 py-1.5 rounded-lg text-sm"
              data-testid="period-select"
            >
              <option value="ytd"   className="text-slate-900">Year-to-Date</option>
              <option value="12m"   className="text-slate-900">Last 12 months</option>
              <option value="month" className="text-slate-900">This month</option>
              <option value="year_2026" className="text-slate-900">Year 2026</option>
              <option value="year_2025" className="text-slate-900">Year 2025</option>
            </select>
            {branches.length > 1 && (
              <select
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                className="bg-white/15 border border-white/30 text-white px-3 py-1.5 rounded-lg text-sm"
                data-testid="branch-select"
              >
                <option value="" className="text-slate-900">All branches</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id} className="text-slate-900">{b.name}</option>
                ))}
              </select>
            )}
            {data?.is_staff_view && (
              <select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="bg-white/15 border border-white/30 text-white px-3 py-1.5 rounded-lg text-sm"
                data-testid="customer-select"
              >
                <option value="" className="text-slate-900">All customers</option>
                {customerOptions.map(c => (
                  <option key={c} value={c} className="text-slate-900">{c}</option>
                ))}
              </select>
            )}
            <Button
              onClick={fetchDashboard}
              variant="outline"
              size="sm"
              className="bg-white/15 border-white/30 text-white hover:bg-white/25"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              onClick={sendReport}
              disabled={sending}
              size="sm"
              className="bg-white text-green-700 hover:bg-green-50"
              data-testid="send-report-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
              Email me this report
            </Button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Kpi icon={MapPin}          label={`Distance (${period.toUpperCase()})`}    accent="indigo" value={`${Math.round(k.distance_km).toLocaleString()} km`} />
        <Kpi icon={Package}         label={`Weight (${period.toUpperCase()})`}      accent="purple" value={`${k.weight_tonnes.toLocaleString()} t`} />
        <Kpi icon={Ship}            label={`Shipments (${period.toUpperCase()})`}   accent="blue"   value={k.shipments} subtitle={`${k.shipments_counted} counted · ${k.shipments_incomplete} incomplete`} />
        <Kpi icon={Leaf}             label={`CO₂ Emissions (${period.toUpperCase()})`} accent="red" value={`${k.co2_tonnes.toLocaleString()} t`} />
        <Kpi icon={TreeDeciduous}    label={`# Trees (${period.toUpperCase()})`}    accent="green"  value={k.trees_equivalent.toLocaleString()} subtitle="equiv. annual sequestration" />
        <Kpi icon={Leaf}             label={`CO₂ Offset (${period.toUpperCase()})`} accent="emerald" value={`${k.co2_offset_tonnes.toFixed(2)} t`} subtitle="link offset projects to grow this" />
      </div>

      {/* Donuts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Movement count by mode" testId="chart-movements">
          {movementsByMode.length === 0 ? (
            <EmptyChart label="No counted movements yet" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={movementsByMode} dataKey="value" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {movementsByMode.map((e, i) => (
                    <Cell key={i} fill={MODE_COLORS[e.name] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend formatter={(v) => <span className="capitalize text-xs">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Emissions breakdown (CO₂e)" testId="chart-emissions">
          {byMode.length === 0 ? (
            <EmptyChart label="No emission data yet" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={byMode} dataKey="value" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {byMode.map((e, i) => (
                    <Cell key={i} fill={MODE_COLORS[e.name] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${(v / 1000).toFixed(2)} t`} />
                <Legend formatter={(v) => <span className="capitalize text-xs">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Weekly stacked bar */}
      <ChartCard title="CO₂ emissions by week (selected period)" testId="chart-weekly">
        {(!data.weekly_co2 || data.weekly_co2.length === 0) ? (
          <EmptyChart label="Not enough data points for weekly trend" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.weekly_co2}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" fontSize={11} angle={-30} textAnchor="end" height={60} interval="preserveStartEnd" />
              <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(1)}t`} />
              <Tooltip formatter={(v) => `${(v / 1000).toFixed(2)} t CO₂e`} />
              <Legend formatter={(v) => <span className="capitalize text-xs">{v}</span>} />
              <Bar dataKey="sea"   stackId="m" fill={MODE_COLORS.sea} />
              <Bar dataKey="air"   stackId="m" fill={MODE_COLORS.air} />
              <Bar dataKey="road"  stackId="m" fill={MODE_COLORS.road} />
              <Bar dataKey="rail"  stackId="m" fill={MODE_COLORS.rail} />
              <Bar dataKey="barge" stackId="m" fill={MODE_COLORS.barge} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Top trade lanes + by destination */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Top trade lanes" testId="top-lanes-table">
          {(!data.top_lanes || data.top_lanes.length === 0) ? (
            <EmptyChart label="No lane data" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b">
                    <th className="text-left py-2">Lane</th>
                    <th className="text-left py-2">Mode</th>
                    <th className="text-right py-2">CO₂e</th>
                    <th className="text-right py-2">% total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_lanes.map((l, i) => {
                    const Icon = MODE_ICONS[l.mode] || Ship;
                    return (
                      <tr key={i} className="border-b last:border-0 text-slate-700">
                        <td className="py-2 text-xs">{l.origin} → {l.destination}</td>
                        <td className="py-2"><Icon className="w-4 h-4 inline mr-1 text-slate-500" /><span className="capitalize text-xs">{l.mode}</span></td>
                        <td className="py-2 text-right text-xs font-medium">{(l.co2_kg / 1000).toFixed(2)} t</td>
                        <td className="py-2 text-right text-xs text-green-700 font-semibold">{l.pct_of_total}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </ChartCard>
        <ChartCard title="Emissions by destination" testId="by-destination">
          {Object.keys(data.by_destination_kg || {}).length === 0 ? (
            <EmptyChart label="No destination data" />
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {Object.entries(data.by_destination_kg)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 15)
                .map(([country, kg]) => {
                  const total = Object.values(data.by_destination_kg).reduce((s, v) => s + v, 0) || 1;
                  const pct = Math.round((kg / total) * 100);
                  return (
                    <div key={country}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-700">{country}</span>
                        <span className="text-slate-500">{(kg / 1000).toFixed(2)} t · {pct}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </ChartCard>
      </div>

      {/* Top customers — staff view only */}
      {data.is_staff_view && data.top_customers && data.top_customers.length > 0 && (
        <ChartCard title="Top customers by CO₂ emissions" testId="top-customers-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b">
                  <th className="text-left py-2">#</th>
                  <th className="text-left py-2">Customer</th>
                  <th className="text-right py-2">Shipments</th>
                  <th className="text-right py-2">CO₂e</th>
                  <th className="text-right py-2">% total</th>
                </tr>
              </thead>
              <tbody>
                {data.top_customers.map((c, i) => (
                  <tr key={i} className="border-b last:border-0 text-slate-700 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setCustomerFilter(c.name)}
                      data-testid={`top-customer-row-${i}`}>
                    <td className="py-2 text-xs text-slate-400">{i + 1}</td>
                    <td className="py-2 text-xs font-medium">{c.name}</td>
                    <td className="py-2 text-right text-xs">{c.shipments}</td>
                    <td className="py-2 text-right text-xs font-semibold">{(c.co2_kg / 1000).toFixed(2)} t</td>
                    <td className="py-2 text-right text-xs text-green-700 font-semibold">{c.pct_of_total}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-400 mt-2">Click any row to filter the whole dashboard to that customer.</p>
          </div>
        </ChartCard>
      )}

      {/* Methodology footer */}
      <div className="bg-white rounded-xl p-4 border shadow-sm text-xs text-slate-500 leading-relaxed">
        <p className="font-semibold text-slate-700 mb-1">Methodology &amp; glossary</p>
        <p>
          <b>Carbon Sequestration:</b> Capturing &amp; storing atmospheric CO₂ through natural processes (e.g. trees).&nbsp;
          <b>Carbon Avoidance:</b> Preventing new greenhouse-gas emissions via renewable energy or efficiency projects.&nbsp;
          <b>Carbon Insetting:</b> Reducing emissions within a company's own supply chain.&nbsp;
          <b>Carbon Offsetting:</b> Compensating emissions by funding external projects that reduce or remove greenhouse gases.
        </p>
        <p className="mt-2">
          Emission factors from the <a className="text-green-700 underline" href="https://www.smartfreightcentre.org/en/our-programs/global-logistics-emissions-council/" target="_blank" rel="noreferrer">GLEC Framework v3.0</a>:
          sea 13, air 500, road 62, rail 22, barge 31 g CO₂e per tonne·km. Distance via haversine ×
          route multiplier (sea ×1.30, air ×1.00, road ×1.25, rail ×1.15).
        </p>
      </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid={testId}>
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="text-center py-10 text-slate-400">
      <Leaf className="w-8 h-8 mx-auto mb-2 opacity-50" />
      <p className="text-sm">{label}</p>
    </div>
  );
}
